"use client";
import ShowcaseInlineCard from "../ShowcaseInlineCard";
import { type PropsWithChildren, useRef, type ReactNode } from "react";
import Img from "../UI/Img";
import { cn } from "~/lib/utils";
import type {
  ShowcaseCardProps,
  ShowcasedItem,
} from "../ShowcaseInlineCard/types";
import useIsIntersecting from "~/lib/hooks/is-intersecting";
import { type LinkProps } from "../UI/Link/types";
import Link from "../UI/Link";

function AnimatedShowcaseInlineCard(
  props: ShowcaseCardProps & {
    index: number;
  },
) {
  const ref = useRef<HTMLDivElement>(null);

  const isIntersecting = useIsIntersecting({
    ref,
    intersectionObserverInit: {
      threshold: 0,
    },
  });

  return (
    <ShowcaseInlineCard
      {...props}
      ref={ref}
      className={
        cn(
          "transition-all duration-500 ease-in-out delay-300",
          !isIntersecting ? "-translate-y-full opacity-0" : "opacity-100",
        )
        // "transition-opacity duration-700 ease-in-out delay-300",
        // isIntersecting ? "opacity-100" : "opacity-0",
      }
      style={{
        transitionDelay: `${props.index * 0.1}s`,
        transformStyle: "preserve-3d",
      }}
    />
  );
}

export default function ShowcaseSection(props: {
  title: ReactNode;
  description?: string;
  items: ShowcasedItem[];
  actionLinks: PropsWithChildren<LinkProps>[];
  img: { src: string; alt: string };
  theme?: "dark";
}) {
  const headRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  const isHeaderIntersecting = useIsIntersecting({
    ref: headRef,
    intersectionObserverInit: {
      threshold: 0.25,
    },
  });

  const isDescriptionIntersecting = useIsIntersecting({
    ref: descriptionRef,
    intersectionObserverInit: {
      threshold: 0.25,
    },
  });

  return (
    <section
      className={cn(
        props.theme === "dark" && "dark bg-primary py-16 max-md:text-center",
      )}
    >
      <div
        className={cn(
          "max-w-[1720px] mx-auto",
          "flex px-8 gap-x-16 gap-y-12 items-center lg:flex-row flex-col-reverse",
          "max-sm:items-center max-sm:text-center",
        )}
      >
        <div
          className="w-1/2 overflow-hidden flex justify-center items-center lg:translate-y-[-5%] lg:translate-x-0 lg:scale-125"
          style={{
            transformOrigin: "right center",
          }}
        >
          <Img
            {...props.img}
            width={1024}
            height={1024}
            className="object-contain lg:translate-x-1/4 lg:translate-y-[5%] scale-75 h-full"
          />
        </div>
        <div className="flex flex-col gap-y-8">
          <div className="flex flex-col gap-y-2">
            <h2
              className={cn(
                "text-4xl.5 leading-tight font-minion-pro",
                "transition-all duration-500 ease-in-out delay-300",
                !isHeaderIntersecting
                  ? "-translate-y-full opacity-0"
                  : "opacity-100",
              )}
              ref={headRef}
            >
              {props.title}
            </h2>
            {props.description && (
              <p
                className={cn(
                  "text-lg max-w-[470px]",
                  "transition-all duration-500 ease-in-out delay-300",
                  !isDescriptionIntersecting
                    ? "-translate-y-full opacity-0"
                    : "opacity-100",
                  "max-lg:text-center",
                )}
                ref={descriptionRef}
              >
                {props.description}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-y-8">
            {props.items.map((reason, index) => (
              <AnimatedShowcaseInlineCard
                theme={props.theme}
                key={reason.title}
                title={reason.title}
                description={reason.description}
                icon={reason.icon}
                index={index}
              />
            ))}
          </div>

          <div className="flex gap-x-6 gap-y-4 mt-2 mx-auto flex-wrap justify-center">
            {props.actionLinks.map((item, index) => (
              <Link
                key={
                  typeof item.href === "string"
                    ? item.href
                    : (item.href.pathname ?? index)
                }
                {...item}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
